import { AttendanceType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/attendance_type_pb'
import { PaymentCollectionType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/payment_collection_type_pb'
import { EventType } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_pb'
import { EventTypeSchedule } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_schedule_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import { CrossLargeIcon } from '@fingertip/icons'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BookerEventTypeItem } from '@/components/booking/booker/components/booker-event-type-item'
import { BookingSelectionSummary } from '@/components/booking/booker/components/booking-selection-summary'
import { Button } from '@/components/ui/button'
import { useBookingSelection } from '@/lib/hooks/use-booking-selection'
import { BookingStage } from '@/lib/stores/booker-store'
import { RootStoreContext } from '@/lib/stores/root-store'

type Props = {
  eventTypes?: EventType[]
  eventTypeSchedules?: EventTypeSchedule[]
  storeConfig?: StoreConfig
  nextStage: BookingStage | null
  isEmbedMode?: boolean
}

export const BookerStageEventTypePicker = observer(
  ({ eventTypes, eventTypeSchedules, storeConfig, isEmbedMode }: Props) => {
    const { t } = useTranslation()
    const {
      bookerStore: {
        setPaymentCollectionType,
        bookingItemsData,
        addBookingItem,
        removeBookingItem,
        removeAllOfBookingItem,
        removeAllBookingItems,
      },
    } = useContext(RootStoreContext)

    const bookingSelection = useBookingSelection(bookingItemsData)

    useEffect(() => {
      setPaymentCollectionType(storeConfig?.paymentCollectionType!)
    }, [setPaymentCollectionType, storeConfig?.paymentCollectionType])

    const hasSelected = useMemo(() => {
      return bookingItemsData.length > 0
    }, [bookingItemsData])

    const hasSelectedMeetings = useMemo(() => {
      return bookingItemsData.some((item) => {
        return item.eventType.attendanceType === AttendanceType.MEETING
      })
    }, [bookingItemsData])

    const hasSelectedGroup = useMemo(() => {
      return bookingItemsData.some((item) => {
        return item.eventType.attendanceType === AttendanceType.GROUP
      })
    }, [bookingItemsData])

    const filteredEventTypes: {
      eventType: EventType
      eventTypeSchedules: EventTypeSchedule[]
    }[] = useMemo(() => {
      let evtTypes = eventTypes || []
      if (eventTypes && hasSelectedMeetings) {
        evtTypes = eventTypes.filter((eventType) => {
          return eventType.attendanceType === AttendanceType.MEETING
        })
      }
      return evtTypes.map((eventType) => {
        return {
          eventType,
          eventTypeSchedules:
            eventTypeSchedules?.filter(
              (schedule) => schedule.eventTypeId === eventType.id,
            ) || [],
        }
      })
    }, [eventTypeSchedules, eventTypes, hasSelectedMeetings])

    const onSelect = useCallback(
      (eventType: EventType) => {
        removeAllBookingItems()
        addBookingItem(eventType)
      },
      [addBookingItem, removeAllBookingItems],
    )

    const onDeselect = useCallback(
      (eventType: EventType) => {
        removeAllOfBookingItem(eventType.id)
      },
      [removeAllOfBookingItem],
    )

    const onAdd = useCallback(
      (eventType: EventType, quantity?: number) => {
        addBookingItem(eventType, quantity)
      },
      [addBookingItem],
    )

    const onRemove = useCallback(
      (eventType: EventType, quantity?: number) => {
        removeBookingItem(eventType.id, quantity)
      },
      [removeBookingItem],
    )

    const onStartOver = useCallback(() => {
      removeAllBookingItems()
    }, [removeAllBookingItems])

    return (
      <div className="size-full">
        <div className="mb-4 flex min-h-9 flex-row items-center justify-between">
          <div className="h5">
            {hasSelected && !hasSelectedGroup
              ? t(t('choose_additional_ev'))
              : t('choose_an_event_type')}
          </div>

          {hasSelected && (
            <Button variant="ghost" size="sm" onClick={onStartOver}>
              {t('clear')}
              <CrossLargeIcon className="ml-1 size-4" />
            </Button>
          )}
        </div>

        <div className="flex flex-col space-y-4">
          {(filteredEventTypes || []).map(
            ({ eventTypeSchedules, eventType }) => {
              const willCheckout =
                (storeConfig?.paymentCollectionType ===
                  PaymentCollectionType.CAPTURE_DETAILS ||
                  storeConfig?.paymentCollectionType ===
                    PaymentCollectionType.COLLECT_UPFRONT) &&
                eventType.priceInCents > 0
              const disableForEmbedMode = !!isEmbedMode && willCheckout

              return (
                <BookerEventTypeItem
                  count={
                    bookingItemsData.find(
                      (item: any) => item.eventTypeId === eventType.id,
                    )?.quantity || 0
                  }
                  onSelect={onSelect}
                  onDeselect={onDeselect}
                  onAdd={onAdd}
                  onRemove={onRemove}
                  eventType={eventType}
                  storeConfig={storeConfig}
                  key={eventType.id}
                  showQuantityPicker={!hasSelectedGroup}
                  unavailable={
                    eventTypeSchedules.length === 0 || disableForEmbedMode
                  }
                />
              )
            },
          )}
        </div>

        {!!bookingSelection && !hasSelectedGroup && (
          <BookingSelectionSummary
            bookingSelection={bookingSelection}
            storeConfig={storeConfig}
          />
        )}
      </div>
    )
  },
)
